<template>
	<v-card outlined>
		<v-card-title class="pb-1">
			{{ brand.name }}
			<v-spacer></v-spacer>
			<v-btn text color="primary" data-tour="configure" @click="$emit('configure')">
				<v-icon left>mdi-cog-outline</v-icon>
				Impostazioni e regole
			</v-btn>
		</v-card-title>
		<v-card-subtitle class="pb-2">
			<a v-if="sito" :href="sito" target="_blank" rel="noopener noreferrer">{{ sito }}</a>
			<span v-else>Nessun sito impostato</span>
		</v-card-subtitle>

		<v-card-text>
			<v-alert v-if="config.status === 'blocked'" type="error" dense outlined>
				Il sito ha bloccato la scansione ({{ config.blockedReason }}). Non insistiamo: chiedi alla marca un feed prodotti, oppure cambia modalità nelle impostazioni e riprova.
			</v-alert>
			<v-alert v-else-if="config.status === 'requires_browser'" type="warning" dense outlined>
				Le pagine non contengono i dati prodotto nell'HTML: imposta la modalità "Browser" nelle impostazioni.
			</v-alert>
			<v-alert v-if="brand.draftRules && !brand.activeRules" type="info" dense outlined>
				Ci sono regole in bozza (v{{ brand.draftRules.version }}): controllale in "Impostazioni e regole" e attivale.
			</v-alert>
			<v-alert v-if="brand.brokenRules" type="warning" dense outlined> Il sito è cambiato rispetto alle regole: controlla le versioni delle regole. </v-alert>

			<div v-if="job" class="mb-3">
				<div class="d-flex align-center">
					<strong class="mr-2">{{ tipoJob }}</strong>
					<v-chip x-small :color="statoJob.colore" dark>{{ statoJob.testo }}</v-chip>
					<v-spacer></v-spacer>
					<span class="caption grey--text">{{ job.created_at | moment('DD/MM/YYYY HH:mm') }}</span>
				</div>
				<v-progress-linear v-if="inCorso" :value="percentuale" :indeterminate="!percentuale" color="primary" class="my-2" height="6"></v-progress-linear>
				<div class="caption">
					<span v-if="c.urls">Pagine: {{ c.processed || 0 }}/{{ c.urls }} · </span>
					<span v-if="c.new !== undefined">nuovi {{ c.new || 0 }} · </span>
					<span v-if="c.updated !== undefined">aggiornati {{ c.updated || 0 }} · </span>
					<span v-if="c.unchanged !== undefined">invariati {{ c.unchanged || 0 }} · </span>
					<span v-if="c.imported !== undefined">importati {{ c.imported || 0 }} · </span>
					<span v-if="c.skipped">saltati {{ c.skipped }} · </span>
					<span v-if="c.errors">errori {{ c.errors }}</span>
				</div>
				<div v-if="job.message" class="caption mt-1">{{ job.message }}</div>
				<div v-if="job.error && !inCorso" class="caption error--text mt-1">{{ testoErrore(job.error) }}</div>
				<!-- pagina dove la scansione si e' fermata (cambio del sito, blocco, errore, annullamento) -->
				<div v-if="job.stoppedUrl && !inCorso" class="caption mt-1">
					Si è fermata alla pagina {{ (job.stoppedAt || 0) + 1 }}:
					<a :href="job.stoppedUrl" target="_blank" rel="noopener noreferrer" class="pagina">{{ job.stoppedUrl }}</a>
				</div>
			</div>

			<div v-if="ripresa && !inCorso" class="caption mb-3">
				<v-icon x-small class="mr-1">mdi-play-pause</v-icon>
				Scansione interrotta a pagina {{ ripresa.pos + 1 }} di {{ ripresa.total }}: «Riprendi la scansione» continua da lì
				<template v-if="ripresa.url && ripresa.url !== (job && job.stoppedUrl)">
					(<a :href="ripresa.url" target="_blank" rel="noopener noreferrer" class="pagina">{{ ripresa.url }}</a>)
				</template>
			</div>

			<pagine-da-verificare :brand-id="brand._id" :config="config" @changed="$emit('pages-changed', $event)" @message="(t, tipo) => $emit('message', t, tipo)" />
		</v-card-text>

		<v-card-actions class="flex-wrap">
			<v-btn v-if="inCorso" outlined color="error" @click="$emit('cancel', job._id)">
				<v-icon left>mdi-stop</v-icon>
				Annulla
			</v-btn>
			<template v-else>
				<v-btn outlined color="primary" :disabled="!openaiActive" data-tour="generate" @click="$emit('generate')">
					<v-icon left>mdi-robot-outline</v-icon>
					{{ brand.activeRules ? 'Rigenera regole con AI' : 'Genera regole con AI' }}
				</v-btn>
				<v-btn color="primary" depressed :disabled="!brand.activeRules" data-tour="scan" @click="$emit('scan')">
					<v-icon left>mdi-magnify-scan</v-icon>
					{{ ripresa ? 'Riprendi la scansione' : 'Scansiona ora' }}
				</v-btn>
				<v-btn v-if="ripresa" text color="primary" :disabled="!brand.activeRules" @click="$emit('scan-fresh')">Ricomincia da capo</v-btn>
			</template>
		</v-card-actions>
	</v-card>
</template>

<script>
import { STATI_JOB, TIPI_JOB, attivo, testoErrore } from './testi';
import PagineDaVerificare from './PagineDaVerificare.vue';

export default {
	name: 'BrandImportBrandPanel',
	components: { PagineDaVerificare },
	props: {
		brand: { type: Object, required: true },
		job: { type: Object, default: null },
		openaiActive: { type: Boolean, default: false },
	},
	computed: {
		config() {
			return this.brand.config || {};
		},
		// punto raggiunto da una scansione interrotta: il worker riparte da li'
		ripresa() {
			return this.config.scanResume || null;
		},
		sito() {
			const url = this.config.baseUrl || this.brand.url;
			if (!url) return '';
			return /^https?:\/\//i.test(url) ? url : `https://${url}`;
		},
		c() {
			return (this.job && this.job.counters) || {};
		},
		inCorso() {
			return attivo(this.job);
		},
		percentuale() {
			return this.c.urls ? Math.round(((this.c.processed || 0) / this.c.urls) * 100) : 0;
		},
		tipoJob() {
			return TIPI_JOB[this.job.type] || this.job.type;
		},
		statoJob() {
			return STATI_JOB[this.job.status] || { testo: this.job.status, colore: 'grey' };
		},
	},
	methods: { testoErrore },
};
</script>

<style scoped>
.pagina {
	word-break: break-all;
}
</style>
